import { useState, useEffect, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents, useMap } from "react-leaflet";
import { MapLibreTileLayer } from "../components/MapLibreTileLayer";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, Plus, Send, X, Sparkles, Info, Filter, Share2, Calendar, Users, Search, Navigation, Loader2, Trash2, Edit3, Star, CreditCard, UserCircle, Globe, Sun, Moon, Mail, ChevronDown, Instagram, Youtube } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { useTranslation } from "../i18n";
import { useTheme } from "../theme";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Local storage key for user's pins
const USER_PINS_KEY = "neuromap_user_pins";
const USER_PIN_TOKENS_KEY = "neuromap_user_pin_tokens";

// Get user's pin IDs from localStorage
const getUserPinIds = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_PINS_KEY) || "[]");
  } catch {
    return [];
  }
};

// Get token map { pinId: ownerToken }
const getUserPinTokens = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_PIN_TOKENS_KEY) || "{}");
  } catch {
    return {};
  }
};

const getOwnerTokenFor = (pinId) => {
  const tokens = getUserPinTokens();
  return tokens[pinId] || null;
};

// Save user's pin ID + token to localStorage
const saveUserPinId = (pinId, ownerToken) => {
  const pins = getUserPinIds();
  if (!pins.includes(pinId)) {
    pins.push(pinId);
    localStorage.setItem(USER_PINS_KEY, JSON.stringify(pins));
  }
  if (ownerToken) {
    const tokens = getUserPinTokens();
    tokens[pinId] = ownerToken;
    localStorage.setItem(USER_PIN_TOKENS_KEY, JSON.stringify(tokens));
  }
};

// Remove pin ID + token from localStorage
const removeUserPinId = (pinId) => {
  const pins = getUserPinIds().filter(id => id !== pinId);
  localStorage.setItem(USER_PINS_KEY, JSON.stringify(pins));
  const tokens = getUserPinTokens();
  delete tokens[pinId];
  localStorage.setItem(USER_PIN_TOKENS_KEY, JSON.stringify(tokens));
};

// Area cluster icon - shows count, not exact location
const createAreaIcon = (count, colors = [], hasEvents = false, hasFeaturedEvents = false) => {
  const baseSize = Math.min(70, 45 + count * 3);
  const size = hasFeaturedEvents ? baseSize + 8 : baseSize;
  
  let background;
  if (hasFeaturedEvents) {
    background = "linear-gradient(135deg, #F59E0B 0%, #EF4444 50%, #F59E0B 100%)";
  } else if (colors.length === 0) {
    background = "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)";
  } else if (colors.length === 1) {
    background = colors[0];
  } else {
    const stops = colors.map((c, i) => `${c} ${(i / (colors.length - 1)) * 100}%`).join(", ");
    background = `linear-gradient(135deg, ${stops})`;
  }
  
  if (hasEvents && !hasFeaturedEvents) {
    background = `linear-gradient(135deg, #10B981 0%, #4F46E5 100%)`;
  }
  
  const borderColor = hasFeaturedEvents ? "#F59E0B" : "white";
  const borderWidth = hasFeaturedEvents ? 5 : 4;
  const glow = hasFeaturedEvents ? "0 0 20px rgba(245,158,11,0.5), 0 4px 20px rgba(0,0,0,0.25)" : "0 4px 20px rgba(0,0,0,0.25)";
  
  return L.divIcon({
    className: "area-cluster-icon",
    html: `
      <div style="
        min-width: ${size}px;
        height: ${size}px;
        border-radius: ${size}px;
        background: ${background};
        border: ${borderWidth}px solid ${borderColor};
        box-shadow: ${glow};
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 900;
        font-family: 'Nunito', sans-serif;
        color: white;
        font-size: ${Math.min(22, 14 + count * 2)}px;
        padding: 0 12px;
        position: relative;
        text-shadow: 0 1px 2px rgba(0,0,0,0.3);
        ${hasFeaturedEvents ? 'animation: pulse-glow 2s infinite;' : ''}
      ">
        ${count}
        ${hasFeaturedEvents ? `<div style="position:absolute;top:-6px;right:-6px;width:22px;height:22px;background:linear-gradient(135deg,#F59E0B,#EF4444);border-radius:50%;border:2px solid white;display:flex;align-items:center;justify-content:center;">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="white"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
        </div>` : hasEvents ? `<div style="position:absolute;top:-4px;right:-4px;width:18px;height:18px;background:#10B981;border-radius:50%;border:2px solid white;display:flex;align-items:center;justify-content:center;">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"/></svg>
        </div>` : ''}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// Map click handler
const MapClickHandler = ({ onMapClick, isAddingPin, onDismissPanels }) => {
  useMapEvents({
    click: (e) => {
      if (isAddingPin) {
        onMapClick(e.latlng);
      } else {
        onDismissPanels();
      }
    },
  });
  return null;
};

// Fly to location
const FlyToLocation = ({ position, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, zoom || 10, { duration: 1.5 });
    }
  }, [position, zoom, map]);
  return null;
};

export default function MapPage() {
  const { t, lang, setLang, languages } = useTranslation();
  const { isDark, toggle: toggleTheme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [labels, setLabels] = useState([]);
  const [pins, setPins] = useState([]);
  const [areaData, setAreaData] = useState([]);
  const [events, setEvents] = useState([]);
  const [userPins, setUserPins] = useState([]);
  const [isAddingPin, setIsAddingPin] = useState(false);
  const [isEditingPin, setIsEditingPin] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [locationInfo, setLocationInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showMyPins, setShowMyPins] = useState(false);
  const [requestName, setRequestName] = useState("");
  const [requestReason, setRequestReason] = useState("");
  const [showLegend, setShowLegend] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [filterLabels, setFilterLabels] = useState([]);
  const [flyToPos, setFlyToPos] = useState(null);
  const [flyToZoom, setFlyToZoom] = useState(null);
  const [selectedAreaDetail, setSelectedAreaDetail] = useState(null);
  const [isPremiumEvent, setIsPremiumEvent] = useState(false);
  const [appSettings, setAppSettings] = useState(null);
  const [showLangMenu, setShowLangMenu] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Event form state
  const [eventForm, setEventForm] = useState({
    title: "", description: "", town: "", country: "", address: "",
    event_type: "meetup", contact_info: "", poster_email: "",
    event_date: "", end_date: "", recurring: false, schedule: ""
  });
  const [eventLocationQuery, setEventLocationQuery] = useState("");
  const [eventLocationResults, setEventLocationResults] = useState([]);
  const [isSearchingEventLocation, setIsSearchingEventLocation] = useState(false);

  const [isPanelMinimized, setIsPanelMinimized] = useState(false);
  // Email signup state (optional during pin drop)
  const [emailSignup, setEmailSignup] = useState({ first_name: "", email: "", consent: false });

  // Load user's pins from localStorage
  useEffect(() => {
    const userPinIds = getUserPinIds();
    if (userPinIds.length > 0) {
      loadUserPins(userPinIds);
    }
  }, [pins]);

  const loadUserPins = async (pinIds) => {
    const userPinsList = pins.filter(p => pinIds.includes(p.id));
    setUserPins(userPinsList);
  };

  // Check for shared area link on mount
  useEffect(() => {
    const town = searchParams.get("town");
    const country = searchParams.get("country");
    if (town && country) {
      loadAreaDetail(town, country);
    }
  }, [searchParams]);

  // Fetch data on mount
  useEffect(() => {
    fetchLabels();
    fetchPins();
    fetchAreaData();
    fetchEvents();
    fetchAppSettings();
  }, []);

  // Refetch when filter changes
  useEffect(() => {
    if (filterLabels.length > 0) {
      fetchFilteredPins();
    } else {
      fetchPins();
    }
    fetchAreaData();
  }, [filterLabels]);

  const fetchLabels = async () => {
    try {
      const res = await axios.get(`${API}/labels`);
      setLabels(res.data);
    } catch (err) {
      console.error("Error fetching labels:", err);
    }
  };

  const fetchPins = async () => {
    try {
      const res = await axios.get(`${API}/pins`);
      setPins(res.data);
    } catch (err) {
      console.error("Error fetching pins:", err);
    }
  };

  const fetchFilteredPins = async () => {
    try {
      const res = await axios.get(`${API}/pins/filter?labels=${filterLabels.join(",")}`);
      setPins(res.data);
    } catch (err) {
      console.error("Error fetching filtered pins:", err);
    }
  };

  const fetchAreaData = async () => {
    try {
      const res = await axios.get(`${API}/pins/area`);
      setAreaData(res.data);
    } catch (err) {
      console.error("Error fetching area data:", err);
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data);
    } catch (err) {
      console.error("Error fetching events:", err);
    }
  };

  const fetchAppSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/public`);
      setAppSettings(res.data);
    } catch (err) {
      console.error("Error fetching settings:", err);
    }
  };

  const loadAreaDetail = async (town, country) => {
    try {
      const res = await axios.get(`${API}/area/${encodeURIComponent(town)}/${encodeURIComponent(country)}`);
      setSelectedAreaDetail(res.data);
      if (res.data.center) {
        setFlyToPos([res.data.center.lat, res.data.center.lng]);
        setFlyToZoom(10);
      }
    } catch (err) {
      toast.error("Area not found");
    }
  };

  // Search for location
  const searchLocation = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    setShowSearchResults(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5&addressdetails=1`
      );
      const data = await res.json();
      setSearchResults(data);
      
      if (data.length === 0) {
        toast.error("No locations found. Try a different search term.");
      }
    } catch (err) {
      toast.error("Search failed. Please try again.");
    }
    setIsSearching(false);
  };

  const selectSearchResult = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    
    setFlyToPos([lat, lng]);
    setFlyToZoom(11); // Don't zoom too close
    setShowSearchResults(false);
    setSearchQuery("");
    
    if (isAddingPin || isEditingPin) {
      handleMapClick({ lat, lng });
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    
    setIsSearching(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setFlyToPos([latitude, longitude]);
        setFlyToZoom(11); // Don't zoom too close
        setIsSearching(false);
        
        if (isAddingPin || isEditingPin) {
          handleMapClick({ lat: latitude, lng: longitude });
        }
        
        toast.success("Found your location!");
      },
      () => {
        setIsSearching(false);
        toast.error("Unable to get your location. Please search manually.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10`
      );
      const data = await res.json();
      const address = data.address || {};
      return {
        town: address.city || address.town || address.village || address.county || address.state || "Unknown",
        country: address.country || "Unknown",
      };
    } catch {
      return { town: "Unknown", country: "Unknown" };
    }
  }, []);

  const handleMapClick = async (latlng) => {
    setSelectedLocation(latlng);
    setIsLoading(true);
    const info = await reverseGeocode(latlng.lat, latlng.lng);
    setLocationInfo(info);
    setIsLoading(false);
  };

  const handleLabelToggle = (labelId) => {
    setSelectedLabels((prev) =>
      prev.includes(labelId) ? prev.filter((id) => id !== labelId) : [...prev, labelId]
    );
  };

  const handleFilterToggle = (labelId) => {
    setFilterLabels((prev) =>
      prev.includes(labelId) ? prev.filter((id) => id !== labelId) : [...prev, labelId]
    );
  };

  const clearFilters = () => {
    setFilterLabels([]);
  };

  const handleSubmitPin = async () => {
    if (!selectedLocation || !locationInfo || selectedLabels.length === 0) {
      toast.error("Please select a location and at least one label");
      return;
    }

    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/pins`, {
        lat: selectedLocation.lat,
        lng: selectedLocation.lng,
        town: locationInfo.town,
        country: locationInfo.country,
        labels: selectedLabels,
      });
      
      saveUserPinId(res.data.id, res.data.owner_token);
      
      // Handle optional email signup — only if all three are present
      if (emailSignup.consent && emailSignup.email && emailSignup.first_name) {
        try {
          await axios.post(`${API}/subscribers`, {
            first_name: emailSignup.first_name,
            email: emailSignup.email,
            consent: true,
          });
          toast.success("Pin added & subscribed to updates!");
        } catch {
          toast.success("Your pin has been added to the map!");
        }
      } else if (emailSignup.consent && (!emailSignup.email || !emailSignup.first_name)) {
        // User ticked consent but didn't fill in details — let them know
        toast.success("Pin added! (Tick the box & fill in your name + email to also get updates)");
      } else {
        toast.success("Your pin has been added to the map!");
      }
      
      setIsAddingPin(false);
      setSelectedLocation(null);
      setSelectedLabels([]);
      setLocationInfo(null);
      setEmailSignup({ first_name: "", email: "", consent: false });
      fetchPins();
      fetchAreaData();
    } catch (err) {
      toast.error("Failed to add pin. Please try again.");
    }
    setIsLoading(false);
  };

  const handleUpdatePin = async () => {
    if (!selectedLocation || !locationInfo || selectedLabels.length === 0) {
      toast.error("Please select a location and at least one label");
      return;
    }

    setIsLoading(true);
    try {
      await axios.put(`${API}/pins/${isEditingPin}`, {
        lat: selectedLocation.lat,
        lng: selectedLocation.lng,
        town: locationInfo.town,
        country: locationInfo.country,
        labels: selectedLabels,
      }, {
        headers: { "X-Pin-Token": getOwnerTokenFor(isEditingPin) || "" }
      });
      
      toast.success("Your pin has been updated!");
      setIsEditingPin(null);
      setSelectedLocation(null);
      setSelectedLabels([]);
      setLocationInfo(null);
      fetchPins();
      fetchAreaData();
    } catch (err) {
      toast.error("Failed to update pin. Please try again.");
    }
    setIsLoading(false);
  };

  const handleDeletePin = async (pinId) => {
    if (!window.confirm("Are you sure you want to remove your pin?")) return;
    
    try {
      await axios.delete(`${API}/pins/${pinId}/user`, {
        headers: { "X-Pin-Token": getOwnerTokenFor(pinId) || "" }
      });
      removeUserPinId(pinId);
      toast.success("Your pin has been removed");
      fetchPins();
      fetchAreaData();
      setShowMyPins(false);
    } catch (err) {
      toast.error("Failed to remove pin");
    }
  };

  const startEditPin = (pin) => {
    setIsEditingPin(pin.id);
    setSelectedLabels(pin.labels);
    setLocationInfo({ town: pin.town, country: pin.country });
    setSelectedLocation({ lat: pin.lat, lng: pin.lng });
    setShowMyPins(false);
  };

  const handleRequestLabel = async () => {
    if (!requestName.trim()) {
      toast.error("Please enter a label name");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/label-requests`, {
        name: requestName.trim(),
        reason: requestReason.trim() || "",
      });
      toast.success("Your label request has been submitted for review!");
      setShowRequestForm(false);
      setRequestName("");
      setRequestReason("");
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to submit request";
      toast.error(message);
    }
    setIsLoading(false);
  };

  // Search for event location using geocoding
  const searchEventLocation = async () => {
    if (!eventLocationQuery.trim()) return;
    setIsSearchingEventLocation(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(eventLocationQuery)}&limit=5&addressdetails=1`
      );
      const data = await res.json();
      setEventLocationResults(data);
      if (data.length === 0) toast.error("No locations found. Try a different search.");
    } catch {
      toast.error("Location search failed.");
    }
    setIsSearchingEventLocation(false);
  };

  const selectEventLocation = (result) => {
    const addr = result.address || {};
    const town = addr.city || addr.town || addr.village || addr.county || addr.state || "Unknown";
    const country = addr.country || "Unknown";
    const address = result.display_name || "";
    setEventForm({ ...eventForm, town, country, address });
    setEventLocationQuery("");
    setEventLocationResults([]);
  };

  const handleSubmitEvent = async () => {
    if (!eventForm.title.trim() || !eventForm.town.trim() || !eventForm.contact_info.trim() || !eventForm.poster_email.trim() || !eventForm.event_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Validate email format
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(eventForm.poster_email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Validate recurring end_date max 6 months
    if (eventForm.recurring && eventForm.end_date) {
      const start = new Date(eventForm.event_date);
      const end = new Date(eventForm.end_date);
      const maxEnd = new Date(start);
      maxEnd.setMonth(maxEnd.getMonth() + 6);
      if (end > maxEnd) {
        toast.error("Recurring events can be a maximum of 6 months");
        return;
      }
    }

    // If premium/featured event, redirect to host portal
    if (isPremiumEvent) {
      toast.info("Redirecting to Event Host portal to set up your featured event...");
      window.location.href = "/host";
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/events`, eventForm);
      toast.success("Your event has been submitted for approval!");
      setShowEventForm(false);
      setEventForm({
        title: "", description: "", town: "", country: "", address: "",
        event_type: "meetup", contact_info: "", poster_email: "",
        event_date: "", end_date: "", recurring: false, schedule: ""
      });
      setEventLocationQuery("");
      setEventLocationResults([]);
      setIsPremiumEvent(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit event");
    }
    setIsLoading(false);
  };

  const shareArea = (area) => {
    const url = `${window.location.origin}?town=${encodeURIComponent(area.town)}&country=${encodeURIComponent(area.country)}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };

  const cancelAddPin = () => {
    setIsAddingPin(false);
    setIsEditingPin(null);
    setSelectedLocation(null);
    setSelectedLabels([]);
    setLocationInfo(null);
  };

  const getLabelColor = (labelId) => {
    const label = labels.find((l) => l.id === labelId);
    return label?.color || "#6B7280";
  };

  const getLabelName = (labelId) => {
    const label = labels.find((l) => l.id === labelId);
    return label?.name || labelId;
  };

  const getAreaEvents = (town, country) => {
    return events.filter(
      (e) => e.town.toLowerCase() === town.toLowerCase() && 
             e.country.toLowerCase() === country.toLowerCase()
    );
  };

  const getAreaColors = (area) => {
    const colorSet = new Set();
    Object.keys(area.label_counts || {}).forEach(labelId => {
      colorSet.add(getLabelColor(labelId));
    });
    return Array.from(colorSet);
  };

  const userPinIds = getUserPinIds();

  return (
    <div className="h-full flex flex-col" data-testid="map-page">
      {/* Navigation */}
      <nav className="nav-bar" data-testid="navbar">
        <Link to="/" className="nav-logo shrink-0" data-testid="logo">
          <img src={`${API}/logo`} alt="" className="w-8 h-8 rounded-full object-contain" onError={(e) => { e.target.style.display = "none"; }} />
          <span className="hidden md:inline">{appSettings?.app_name || t("app_name")}</span>
        </Link>
        
        {/* Search Bar */}
        <div className="flex-1 mx-2 relative" style={{ minWidth: 0 }}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="search"
              enterKeyHint="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchLocation()}
              placeholder={t("search_placeholder")}
              className="w-full pl-9 pr-10 py-2 bg-slate-100 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-full text-base transition-all"
              data-testid="search-input"
            />
            <button
              onClick={searchLocation}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-slate-200 rounded-full transition-colors"
              title="Search"
              data-testid="search-submit-btn"
            >
              {isSearching ? (
                <Loader2 className="w-4 h-4 text-indigo-600 animate-spin" />
              ) : (
                <Search className="w-4 h-4 text-indigo-600" />
              )}
            </button>
          </div>
          
          {/* Search Results Dropdown */}
          {showSearchResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-[10001]" data-testid="search-results">
              {searchResults.map((result, idx) => (
                <button
                  key={idx}
                  onClick={() => selectSearchResult(result)}
                  className="w-full px-4 py-3 text-left hover:bg-indigo-50 transition-colors border-b border-slate-100 last:border-0"
                  data-testid={`search-result-${idx}`}
                >
                  <p className="font-medium text-slate-800 text-sm">{result.display_name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {result.address?.postcode && `${result.address.postcode} • `}
                    {result.type}
                  </p>
                </button>
              ))}
              <button
                onClick={() => setShowSearchResults(false)}
                className="w-full px-4 py-2 text-center text-sm text-slate-500 hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => setShowFilter(!showFilter)}
            className={`p-2 hover:bg-slate-100 rounded-full ${filterLabels.length > 0 ? 'ring-2 ring-indigo-500' : ''}`}
            data-testid="toggle-filter-btn"
            title="Filter"
          >
            <Filter className="w-4 h-4 text-slate-600" />
          </button>
          <button
            onClick={toggleTheme}
            className="p-2 hover:bg-slate-100 rounded-full hidden sm:block"
            data-testid="dark-mode-toggle"
            title={isDark ? "Light mode" : "Dark mode"}
          >
            {isDark ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-slate-600" />}
          </button>
          {appSettings?.enable_calendar === true && (
          <Link
            to="/calendar"
            className="p-2 hover:bg-slate-100 rounded-full hidden sm:block"
            data-testid="calendar-link"
            title="Calendar"
          >
            <Calendar className="w-4 h-4 text-slate-600" />
          </Link>
          )}
          <Link
            to="/regions"
            className="p-2 hover:bg-slate-100 rounded-full hidden md:block"
            data-testid="regions-link"
            title="Regions"
          >
            <Users className="w-4 h-4 text-slate-600" />
          </Link>
          {/* Language Selector - hidden until full translations are ready */}
          {/*<div className="relative hidden md:block">
            <button
              onClick={() => setShowLangMenu(!showLangMenu)}
              className="p-2 hover:bg-slate-100 rounded-full"
              data-testid="language-selector-btn"
            >
              <Globe className="w-4 h-4 text-slate-600" />
            </button>
            {showLangMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-[10001] min-w-[140px]" data-testid="language-menu">
                {Object.entries(languages).map(([code, name]) => (
                  <button
                    key={code}
                    onClick={() => { setLang(code); setShowLangMenu(false); }}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-indigo-50 transition-colors ${lang === code ? 'bg-indigo-50 font-bold text-indigo-700' : 'text-slate-700'}`}
                    data-testid={`lang-option-${code}`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>*/}
          {appSettings?.enable_host_portal === true && (
          <Link
            to="/host"
            className="text-amber-600 hover:text-amber-700 font-bold transition-colors items-center gap-1 hidden md:flex px-2"
            data-testid="host-portal-link"
          >
            <Star className="w-3.5 h-3.5" />
            <span className="hidden lg:inline">{t("host")}</span>
          </Link>
          )}
        </div>
      </nav>

      {/* Map Container */}
      <div className="map-wrapper" data-testid="map-wrapper">
        <MapContainer
          center={[25, 15]}
          zoom={3}
          minZoom={3}
          maxZoom={12}
          maxBounds={[[-85, -180], [85, 180]]}
          maxBoundsViscosity={1.0}
          attributionControl={true}
          doubleClickZoom={false}
          style={{ height: "100%", width: "100%", background: "#b3d1ff" }}
          data-testid="map-container"
        >
          <MapLibreTileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors &middot; <a href="https://openfreemap.org" target="_blank" rel="noopener noreferrer">OpenFreeMap</a>'
            url="https://tiles.openfreemap.org/styles/liberty"
          />
          <MapClickHandler onMapClick={handleMapClick} isAddingPin={isAddingPin || isEditingPin} onDismissPanels={() => { setShowFilter(false); setShowLegend(false); }} />
          {flyToPos && <FlyToLocation position={flyToPos} zoom={flyToZoom} />}

          {/* Area markers - Always show */}
          <MarkerClusterGroup
            chunkedLoading
            showCoverageOnHover={false}
            spiderfyOnMaxZoom={true}
            disableClusteringAtZoom={6}
            maxClusterRadius={60}
            iconCreateFunction={(cluster) => {
              const childMarkers = cluster.getAllChildMarkers();
              const total = childMarkers.reduce((sum, m) => sum + (m.options.totalPins || 1), 0);
              // Collect all label colors from children, deduped, preserving order
              const seen = new Set();
              const allColors = [];
              childMarkers.forEach((m) => {
                (m.options.areaColors || []).forEach((c) => {
                  if (!seen.has(c)) { seen.add(c); allColors.push(c); }
                });
              });
              const size = Math.min(70, 40 + Math.floor(total / 5) * 3);
              const fontSize = Math.min(22, 14 + (total > 99 ? 2 : 4));
              let background;
              if (allColors.length === 0) {
                background = "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)";
              } else if (allColors.length === 1) {
                background = allColors[0];
              } else {
                const stops = allColors.map((c, i) => `${c} ${(i / (allColors.length - 1)) * 100}%`).join(", ");
                background = `linear-gradient(135deg, ${stops})`;
              }
              return L.divIcon({
                className: "area-cluster-icon",
                html: `
                  <div style="
                    min-width: ${size}px;
                    height: ${size}px;
                    border-radius: ${size}px;
                    background: ${background};
                    border: 4px solid white;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.25);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 900;
                    font-family: 'Nunito', sans-serif;
                    color: white;
                    font-size: ${fontSize}px;
                    padding: 0 12px;
                    text-shadow: 0 1px 2px rgba(0,0,0,0.4);
                  ">${total}</div>
                `,
                iconSize: [size, size],
                iconAnchor: [size / 2, size / 2],
              });
            }}
          >
          {areaData.map((area, idx) => {
              const areaPins = pins.filter(
                (p) => p.town === area.town && p.country === area.country
              );
              if (areaPins.length === 0) return null;
              
              const avgLat = areaPins.reduce((sum, p) => sum + p.lat, 0) / areaPins.length;
              const avgLng = areaPins.reduce((sum, p) => sum + p.lng, 0) / areaPins.length;
              const areaEvents = getAreaEvents(area.town, area.country);
              const areaColors = getAreaColors(area);
              const hasFeatured = areaEvents.some(e => e.featured);

              return (
                <Marker
                  key={`area-${idx}`}
                  position={[avgLat, avgLng]}
                  icon={createAreaIcon(area.total_pins, areaColors, areaEvents.length > 0, hasFeatured)}
                  data-testid={`area-marker-${idx}`}
                  ref={(layer) => {
                    if (layer) {
                      layer.options.totalPins = area.total_pins;
                      layer.options.areaColors = areaColors;
                    }
                  }}
                >
                  <Popup className="area-popup" data-testid={`area-popup-${idx}`}>
                    <div className="min-w-[300px] p-2">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-bold text-lg text-slate-800" style={{ fontFamily: 'Nunito' }}>
                            {area.town}
                          </h3>
                          <p className="text-slate-500 text-sm">{area.country}</p>
                        </div>
                        <button
                          onClick={() => shareArea(area)}
                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                          data-testid={`share-area-${idx}`}
                        >
                          <Share2 className="w-4 h-4 text-slate-500" />
                        </button>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-2xl font-black text-indigo-600" style={{ fontFamily: 'Nunito' }}>
                          {area.total_pins}
                        </span>
                        <span className="text-slate-600">people in this area</span>
                      </div>
                      
                      <div className="space-y-2 mb-3">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                          Community Breakdown
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(area.label_counts || {}).map(([labelId, count]) => (
                            <span
                              key={labelId}
                              className="label-badge"
                              style={{ backgroundColor: getLabelColor(labelId) }}
                            >
                              {getLabelName(labelId)}: {count}
                            </span>
                          ))}
                        </div>
                      </div>

                      {areaEvents.length > 0 && (
                        <div className="pt-3 border-t border-slate-100">
                          <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Local Events ({areaEvents.length})
                          </p>
                          <div className="space-y-2">
                            {areaEvents.slice(0, 2).map((event) => (
                              <div key={event.id} className={`rounded-lg p-2 ${event.featured ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50'}`}>
                                <div className="flex items-center gap-1">
                                  {event.featured && <Star className="w-3 h-3 text-amber-500" />}
                                  <p className="font-semibold text-sm text-slate-800">{event.title}</p>
                                </div>
                                <p className="text-xs text-slate-500">{event.event_type} • {event.schedule || "Contact for details"}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MarkerClusterGroup>

          {/* Selected location marker when adding/editing */}
          {selectedLocation && (isAddingPin || isEditingPin) && (
            <>
              <Circle
                center={[selectedLocation.lat, selectedLocation.lng]}
                radius={2000}
                pathOptions={{
                  color: "#4F46E5",
                  fillColor: "#4F46E5",
                  fillOpacity: 0.1,
                  weight: 2,
                  dashArray: "5, 10"
                }}
              />
              <Marker
                position={[selectedLocation.lat, selectedLocation.lng]}
                icon={L.divIcon({
                  className: "preview-marker",
                  html: `<div style="
                    width: 20px;
                    height: 20px;
                    background: #4F46E5;
                    border: 3px solid white;
                    border-radius: 50%;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                  "></div>`,
                  iconSize: [20, 20],
                  iconAnchor: [10, 10],
                })}
              />
            </>
          )}
        </MapContainer>

        {/* My Pins Panel */}
        {showMyPins && userPins.length > 0 && (
          <div className="floating-panel floating-panel-left animate-fade-in" data-testid="my-pins-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-slate-800" style={{ fontFamily: 'Nunito' }}>My Pins</h3>
              <button onClick={() => setShowMyPins(false)} className="p-2 hover:bg-slate-100 rounded-xl">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="space-y-3">
              {userPins.map((pin) => (
                <div key={pin.id} className="bg-slate-50 rounded-xl p-3" data-testid={`user-pin-${pin.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-slate-800">{pin.town}</p>
                      <p className="text-sm text-slate-500">{pin.country}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {pin.labels.map(labelId => (
                      <span
                        key={labelId}
                        className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                        style={{ backgroundColor: getLabelColor(labelId) }}
                      >
                        {getLabelName(labelId)}
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEditPin(pin)}
                      className="flex-1 flex items-center justify-center gap-1 py-2 px-3 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg text-sm font-medium transition-colors"
                      data-testid={`edit-pin-${pin.id}`}
                    >
                      <Edit3 className="w-4 h-4" />
                      Move/Edit
                    </button>
                    <button
                      onClick={() => handleDeletePin(pin.id)}
                      className="flex-1 flex items-center justify-center gap-1 py-2 px-3 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium transition-colors"
                      data-testid={`delete-pin-${pin.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter Panel - full screen on mobile, floating on desktop */}
        {showFilter && (
          <>
            {/* Mobile: full screen overlay */}
            <div className="fixed inset-0 z-[10001] bg-white p-6 sm:hidden" data-testid="filter-panel">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-slate-800 text-lg" style={{ fontFamily: 'Nunito' }}>Filter by Label</h3>
                <button onClick={() => setShowFilter(false)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="close-filter-btn">
                  <X className="w-6 h-6 text-slate-500" />
                </button>
              </div>
              <div className="space-y-2 mb-4">
                {labels.map((label) => (
                  <button
                    key={label.id}
                    onClick={() => handleFilterToggle(label.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-base ${
                      filterLabels.includes(label.id) ? 'bg-slate-100 ring-2 ring-indigo-500' : 'hover:bg-slate-50'
                    }`}
                    data-testid={`filter-label-${label.id}`}
                  >
                    <div className="w-5 h-5 rounded-full" style={{ backgroundColor: label.color }} />
                    <span className="font-medium text-slate-700">{label.name}</span>
                    {filterLabels.includes(label.id) && (
                      <span className="ml-auto text-indigo-600 font-semibold">✓</span>
                    )}
                  </button>
                ))}
              </div>
              {filterLabels.length > 0 && (
                <button onClick={clearFilters} className="btn-secondary w-full text-sm" data-testid="clear-filters-btn">
                  Clear Filters
                </button>
              )}
              <button onClick={() => setShowFilter(false)} className="btn-primary w-full mt-4" data-testid="apply-filter-btn">
                Apply & View Map
              </button>
            </div>
            {/* Desktop: floating panel */}
            <div className="floating-panel animate-fade-in hidden sm:block" style={{ top: "1rem", right: "1rem", maxWidth: "320px" }} data-testid="filter-panel-desktop">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>Filter by Label</h3>
                <button onClick={() => setShowFilter(false)} className="p-1 hover:bg-slate-100 rounded-lg" data-testid="close-filter-btn-desktop">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <div className="space-y-2 mb-4">
                {labels.map((label) => (
                  <button
                    key={label.id}
                    onClick={() => handleFilterToggle(label.id)}
                    className={`w-full flex items-center gap-3 p-2 rounded-xl transition-all ${
                      filterLabels.includes(label.id) ? 'bg-slate-100 ring-2 ring-indigo-500' : 'hover:bg-slate-50'
                    }`}
                    data-testid={`filter-label-desktop-${label.id}`}
                  >
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: label.color }} />
                    <span className="font-medium text-slate-700">{label.name}</span>
                    {filterLabels.includes(label.id) && (
                      <span className="ml-auto text-indigo-600 text-sm font-semibold">✓</span>
                    )}
                  </button>
                ))}
              </div>
              {filterLabels.length > 0 && (
                <button onClick={clearFilters} className="btn-secondary w-full text-sm" data-testid="clear-filters-desktop-btn">
                  Clear Filters
                </button>
              )}
            </div>
          </>
        )}

        {/* Info/Legend Panel - Now includes privacy info */}
        {showLegend && (
          <div className="floating-panel animate-fade-in" style={{ maxWidth: "300px", top: showFilter ? "280px" : "1rem", right: "1rem" }} data-testid="legend-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>Info & Legend</h3>
              <button onClick={() => setShowLegend(false)} className="p-1 hover:bg-slate-100 rounded-lg" data-testid="close-legend-btn">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            {/* Privacy Section */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4">
              <p className="font-semibold text-emerald-800 text-sm mb-1">Privacy Protected</p>
              <p className="text-xs text-emerald-700">
                Exact locations are NEVER shown. We only display town/area counts with a random offset. No houses or streets are visible.
              </p>
            </div>
            
            {/* Label Colors */}
            <div className="mb-4">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Label Colors</p>
              <div className="space-y-1.5">
                {labels.map((label) => (
                  <div key={label.id} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: label.color }} />
                    <span className="text-slate-700 text-sm">{label.name}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Map Guide */}
            <div className="pt-3 border-t border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">How It Works</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>• Numbers show people in each area</li>
                <li>• Colors indicate the community mix</li>
                <li>• Green badge = local events available</li>
                <li>• Click an area to see details</li>
              </ul>
            </div>
          </div>
        )}

        {/* Shared Area Detail Panel */}
        {selectedAreaDetail && !isAddingPin && !isEditingPin && (
          <div className="floating-panel floating-panel-left animate-fade-in" data-testid="area-detail-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-xl text-slate-800" style={{ fontFamily: 'Nunito' }}>{selectedAreaDetail.town}</h3>
                <p className="text-slate-500">{selectedAreaDetail.country}</p>
              </div>
              <button onClick={() => { setSelectedAreaDetail(null); setSearchParams({}); }} className="p-2 hover:bg-slate-100 rounded-xl" data-testid="close-area-detail-btn">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-indigo-600" />
              <span className="text-2xl font-black text-indigo-600" style={{ fontFamily: 'Nunito' }}>{selectedAreaDetail.total_pins}</span>
              <span className="text-slate-600">community members</span>
            </div>

            <div className="space-y-3 mb-4">
              <p className="text-xs font-semibold text-slate-500 uppercase">Breakdown</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(selectedAreaDetail.label_counts || {}).map(([labelId, count]) => (
                  <span key={labelId} className="label-badge" style={{ backgroundColor: getLabelColor(labelId) }}>
                    {getLabelName(labelId)}: {count}
                  </span>
                ))}
              </div>
            </div>

            {selectedAreaDetail.events?.length > 0 && (
              <div className="space-y-2 mb-4">
                <p className="text-xs font-semibold text-emerald-600 uppercase flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Local Events
                </p>
                {selectedAreaDetail.events.map((event) => (
                  <div key={event.id} className={`rounded-xl p-3 ${event.featured ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50'}`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      {event.featured && <Star className="w-3.5 h-3.5 text-amber-500" />}
                      <p className="font-bold text-slate-800">{event.title}</p>
                    </div>
                    <p className="text-sm text-slate-600">{event.description}</p>
                    <p className="text-xs text-slate-500 mt-1">{event.event_type} • {event.contact_info}</p>
                  </div>
                ))}
              </div>
            )}

            <button onClick={() => shareArea(selectedAreaDetail)} className="btn-secondary w-full" data-testid="share-detail-btn">
              <Share2 className="w-4 h-4" />
              Share This Area
            </button>
          </div>
        )}

        {/* Welcome / Add Pin Panel - Simplified without privacy notice */}
        {!isAddingPin && !isEditingPin && !showRequestForm && !showEventForm && !selectedAreaDetail && !showMyPins && (
          <div className={`floating-panel floating-panel-left animate-fade-in ${isPanelMinimized ? 'panel-minimized' : ''}`} data-testid="welcome-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl overflow-hidden flex items-center justify-center shrink-0">
                  <img src={`${API}/logo`} alt="" className="w-full h-full object-contain" onError={(e) => { e.target.style.display = "none"; }} />
                </div>
                <div>
                  <h2 className="font-bold text-lg text-slate-800" style={{ fontFamily: 'Nunito' }}>{appSettings?.app_name || t("app_name")}</h2>
                </div>
              </div>
              <button onClick={() => setIsPanelMinimized(!isPanelMinimized)} className="p-2 hover:bg-slate-100 rounded-xl sm:hidden" data-testid="minimize-welcome-btn" title={isPanelMinimized ? "Expand" : "Minimize"}>
                <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${isPanelMinimized ? 'rotate-180' : ''}`} />
              </button>
            </div>
            
            <div className="panel-content">
              <p className="text-slate-600 mb-6 leading-relaxed">
                {appSettings?.hero_tagline || t("connect_text")}
              </p>
              
              <button onClick={() => { setIsAddingPin(true); setIsPanelMinimized(false); }} className="btn-primary w-full" data-testid="start-add-pin-btn"
                disabled={appSettings?.enable_pin_dropping === false}
                title={appSettings?.enable_pin_dropping === false ? "Pin dropping is currently disabled" : ""}
              >
                <Plus className="w-5 h-5" />
                {appSettings?.enable_pin_dropping === false ? "Pin dropping is disabled" : t("add_my_pin")}
              </button>
              
              {userPinIds.length > 0 && (
                <button onClick={() => setShowMyPins(true)} className="btn-secondary w-full mt-3" data-testid="manage-pins-btn">
                  <Edit3 className="w-5 h-5" />
                  {t("manage_pins")} ({userPinIds.length})
                </button>
              )}
              
              {appSettings?.enable_event_submissions === true && (
                <button onClick={() => setShowEventForm(true)} className="btn-secondary w-full mt-3" data-testid="add-event-btn">
                  <Calendar className="w-5 h-5" />
                  {t("add_event")}
                </button>
              )}

              {appSettings?.enable_label_requests === true && (
                <div className="mt-3">
                  {appSettings?.label_request_text && (
                    <p className="text-xs text-slate-500 mb-2">{appSettings.label_request_text}</p>
                  )}
                  <button onClick={() => setShowRequestForm(true)} className="btn-secondary w-full" data-testid="request-label-btn">
                    <Plus className="w-4 h-4" />
                    Request New Label
                  </button>
                </div>
              )}

              {appSettings?.show_social_links === true && (
                (appSettings?.social_tiktok_url || appSettings?.social_instagram_url || appSettings?.social_youtube_url) && (
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-center gap-4" data-testid="social-links-row">
                    {appSettings?.social_tiktok_url && (
                      <a
                        href={appSettings.social_tiktok_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-slate-700 transition-colors"
                        title="TikTok"
                        data-testid="social-tiktok-link"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z"/></svg>
                      </a>
                    )}
                    {appSettings?.social_instagram_url && (
                      <a
                        href={appSettings.social_instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-slate-700 transition-colors"
                        title="Instagram"
                        data-testid="social-instagram-link"
                      >
                        <Instagram className="w-4 h-4" />
                      </a>
                    )}
                    {appSettings?.social_youtube_url && (
                      <a
                        href={appSettings.social_youtube_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-slate-700 transition-colors"
                        title="YouTube"
                        data-testid="social-youtube-link"
                      >
                        <Youtube className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {/* Adding/Editing Pin Form */}
        {(isAddingPin || isEditingPin) && (
          <div className={`floating-panel floating-panel-left animate-fade-in ${isPanelMinimized ? 'panel-minimized' : ''}`} data-testid="add-pin-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-slate-800" style={{ fontFamily: 'Nunito' }}>
                {isEditingPin ? t("edit_pin") : t("add_pin")}
              </h3>
              <div className="flex items-center gap-1">
                <button onClick={() => setIsPanelMinimized(!isPanelMinimized)} className="p-2 hover:bg-slate-100 rounded-xl sm:hidden" data-testid="minimize-panel-btn" title={isPanelMinimized ? "Expand" : "Minimize"}>
                  <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${isPanelMinimized ? 'rotate-180' : ''}`} />
                </button>
                <button onClick={cancelAddPin} className="p-2 hover:bg-slate-100 rounded-xl" data-testid="cancel-add-pin-btn">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>

            <div className="panel-content">

            {/* Step 1: Find Location */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-sm">1</div>
                <span className="font-semibold text-slate-700">Find your area</span>
              </div>
              
              <div className="space-y-2 mb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && searchLocation()}
                    placeholder="Or search postcode/city..."
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border-2 border-slate-200 focus:border-indigo-500 rounded-xl text-sm"
                    data-testid="pin-search-input"
                  />
                </div>
                
                {searchQuery && (
                  <button onClick={searchLocation} className="w-full py-2 bg-slate-200 hover:bg-slate-300 rounded-xl text-sm font-medium transition-colors" data-testid="search-btn">
                    Search
                  </button>
                )}
                
                {showSearchResults && searchResults.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden max-h-48 overflow-y-auto">
                    {searchResults.map((result, idx) => (
                      <button key={idx} onClick={() => selectSearchResult(result)} className="w-full px-3 py-2 text-left hover:bg-indigo-50 transition-colors border-b border-slate-100 last:border-0">
                        <p className="font-medium text-slate-800 text-sm truncate">{result.display_name}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <p className="text-slate-500 text-xs mb-2">Or click on the map</p>
              
              {selectedLocation ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                  <p className="text-emerald-800 font-medium flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {isLoading ? "Finding location..." : `${locationInfo?.town}, ${locationInfo?.country}`}
                  </p>
                </div>
              ) : (
                <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl p-3 text-center">
                  <p className="text-slate-500 text-sm">No location selected yet</p>
                </div>
              )}
            </div>

            {/* Step 2: Select Labels */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-sm">2</div>
                <span className="font-semibold text-slate-700">Select your labels</span>
              </div>
              <div className="label-grid">
                {labels.map((label) => (
                  <button
                    key={label.id}
                    onClick={() => handleLabelToggle(label.id)}
                    className={`label-checkbox ${selectedLabels.includes(label.id) ? "selected" : ""}`}
                    style={{ backgroundColor: label.color }}
                    data-testid={`label-${label.id}`}
                  >
                    {label.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 3: Optional Email Signup */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center font-bold text-sm">3</div>
                <span className="font-semibold text-slate-700">Stay connected (optional)</span>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3 space-y-2" data-testid="email-signup-section">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={emailSignup.consent}
                    onChange={(e) => setEmailSignup({ ...emailSignup, consent: e.target.checked, first_name: e.target.checked ? emailSignup.first_name : "", email: e.target.checked ? emailSignup.email : "" })}
                    className="w-4 h-4 rounded border-slate-300 mt-0.5"
                    data-testid="signup-consent"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                    <span className="font-semibold">Send me community updates & local event alerts.</span>
                    <span className="block text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Tick the box to receive emails. See our{" "}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 font-medium underline" data-testid="signup-privacy-link">
                        Privacy Policy
                      </a>. Unsubscribe anytime.
                    </span>
                  </span>
                </label>
                {emailSignup.consent && (
                  <div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-pink-500" />
                      <p className="text-xs text-slate-500">Enter your details below:</p>
                    </div>
                    <input
                      type="text"
                      value={emailSignup.first_name}
                      onChange={(e) => setEmailSignup({ ...emailSignup, first_name: e.target.value })}
                      placeholder="First name"
                      className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm"
                      data-testid="signup-first-name"
                    />
                    <input
                      type="email"
                      value={emailSignup.email}
                      onChange={(e) => setEmailSignup({ ...emailSignup, email: e.target.value })}
                      placeholder="Email address"
                      className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm"
                      data-testid="signup-email"
                    />
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={isEditingPin ? handleUpdatePin : handleSubmitPin}
              disabled={!selectedLocation || selectedLabels.length === 0 || isLoading}
              className="btn-primary w-full"
              data-testid="submit-pin-btn"
            >
              <Send className="w-5 h-5" />
              {isLoading ? "Saving..." : isEditingPin ? "Update Pin" : "Add Pin"}
            </button>
            
            {isEditingPin && (
              <button
                onClick={() => handleDeletePin(isEditingPin)}
                className="btn-secondary w-full mt-3 text-red-600 hover:bg-red-50"
                data-testid="delete-while-editing-btn"
              >
                <Trash2 className="w-5 h-5" />
                Remove Pin Instead
              </button>
            )}
            </div>
          </div>
        )}

        {/* Request Label Form */}
        {showRequestForm && (
          <div className="floating-panel floating-panel-left animate-fade-in" data-testid="request-form-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-slate-800" style={{ fontFamily: 'Nunito' }}>Request New Label</h3>
              <button onClick={() => setShowRequestForm(false)} className="p-2 hover:bg-slate-100 rounded-xl" data-testid="close-request-form-btn">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <p className="text-slate-600 mb-4 text-sm">Can't see your label? Request a new one.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Label Name</label>
                <input type="text" value={requestName} onChange={(e) => setRequestName(e.target.value)} placeholder="" className="input-field" data-testid="request-name-input" />
              </div>
              <p className="text-xs text-slate-500">Label requests will be considered during each app update.</p>
              <button onClick={handleRequestLabel} disabled={isLoading} className="btn-primary w-full" data-testid="submit-request-btn">
                <Send className="w-5 h-5" />
                {isLoading ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </div>
        )}

        {/* Add Event Form - Now with Premium Option */}
        {showEventForm && (
          <div className="floating-panel floating-panel-left animate-fade-in" style={{ maxHeight: "85vh", overflowY: "auto" }} data-testid="event-form-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-slate-800" style={{ fontFamily: 'Nunito' }}>Add Community Event</h3>
              <button onClick={() => setShowEventForm(false)} className="p-2 hover:bg-slate-100 rounded-xl" data-testid="close-event-form-btn">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            {/* Event Type Selection - Free vs Premium */}
            <div className="mb-4">
              <div className={`grid ${appSettings?.enable_featured_listings !== false ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}>
                <button
                  onClick={() => setIsPremiumEvent(false)}
                  className={`p-3 rounded-xl border-2 transition-all ${!isPremiumEvent ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid="free-event-btn"
                >
                  <p className="font-semibold text-slate-800">Free Listing</p>
                  <p className="text-xs text-slate-500">Basic visibility</p>
                </button>
                {appSettings?.enable_featured_listings !== false && (
                <button
                  onClick={() => setIsPremiumEvent(true)}
                  className={`p-3 rounded-xl border-2 transition-all relative ${isPremiumEvent ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid="premium-event-btn"
                >
                  <Star className="w-4 h-4 text-amber-500 absolute top-2 right-2" />
                  <p className="font-semibold text-slate-800">Featured</p>
                  <p className="text-xs text-slate-500">Top placement</p>
                </button>
                )}
              </div>
              
              {isPremiumEvent && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-amber-600" />
                    <p className="font-semibold text-amber-800 text-sm">Featured Event - £5/month</p>
                  </div>
                  <ul className="text-xs text-amber-700 space-y-1">
                    <li>• Highlighted at top of area listings</li>
                    <li>• Special badge on map markers</li>
                    <li>• Priority in search results</li>
                    <li>• Analytics dashboard access</li>
                  </ul>
                  <p className="text-xs text-amber-600 mt-2 font-medium">You'll be redirected to create a host account</p>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Event Title *</label>
                <input type="text" value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} placeholder="e.g., ADHD Support Group" className="input-field" data-testid="event-title-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Your Email *</label>
                <input type="email" value={eventForm.poster_email} onChange={(e) => setEventForm({ ...eventForm, poster_email: e.target.value })} placeholder="We'll notify you about your event status" className="input-field" data-testid="event-poster-email-input" />
                <p className="text-xs text-slate-400 mt-1">Not displayed publicly. Used for approval notifications only.</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
                <textarea value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} placeholder="What is this event about?" className="textarea-field" data-testid="event-description-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Event Location *</label>
                {eventForm.town ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="event-location-display">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        <MapPin className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                        <span className="font-medium text-emerald-800 text-sm">{eventForm.address || `${eventForm.town}, ${eventForm.country}`}</span>
                      </div>
                      <button onClick={() => setEventForm({ ...eventForm, town: "", country: "", address: "" })} className="text-emerald-600 hover:text-emerald-800 text-sm font-medium shrink-0" data-testid="change-event-location-btn">Change</button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={eventLocationQuery}
                        onChange={(e) => setEventLocationQuery(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && searchEventLocation()}
                        placeholder="Search postcode, city, or address..."
                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border-2 border-slate-200 focus:border-indigo-500 rounded-xl text-sm"
                        data-testid="event-location-search-input"
                      />
                    </div>
                    {eventLocationQuery && (
                      <button onClick={searchEventLocation} disabled={isSearchingEventLocation} className="w-full py-2 bg-slate-200 hover:bg-slate-300 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2" data-testid="event-location-search-btn">
                        {isSearchingEventLocation ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        Search location
                      </button>
                    )}
                    {eventLocationResults.length > 0 && (
                      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden max-h-48 overflow-y-auto" data-testid="event-location-results">
                        {eventLocationResults.map((result, idx) => (
                          <button key={idx} onClick={() => selectEventLocation(result)} className="w-full px-3 py-2 text-left hover:bg-indigo-50 transition-colors border-b border-slate-100 last:border-0" data-testid={`event-location-result-${idx}`}>
                            <p className="font-medium text-slate-800 text-sm truncate">{result.display_name}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Event Date *</label>
                <input type="date" value={eventForm.event_date} onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })} min={new Date().toISOString().split('T')[0]} className="input-field" data-testid="event-date-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Event Type</label>
                <select value={eventForm.event_type} onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })} className="input-field" data-testid="event-type-select">
                  <option value="meetup">Meetup</option>
                  <option value="support_group">Support Group</option>
                  <option value="workshop">Workshop</option>
                  <option value="social">Social Event</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Contact Info *</label>
                <input type="text" value={eventForm.contact_info} onChange={(e) => setEventForm({ ...eventForm, contact_info: e.target.value })} placeholder="Public contact — email or website" className="input-field" data-testid="event-contact-input" />
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={eventForm.recurring} onChange={(e) => setEventForm({ ...eventForm, recurring: e.target.checked, end_date: "" })} className="w-4 h-4 rounded border-slate-300" data-testid="event-recurring-checkbox" />
                  <span className="text-sm font-medium text-slate-700">Recurring event</span>
                </label>
              </div>
              {eventForm.recurring && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Schedule</label>
                    <input type="text" value={eventForm.schedule} onChange={(e) => setEventForm({ ...eventForm, schedule: e.target.value })} placeholder="e.g., Every Tuesday 7pm" className="input-field" data-testid="event-schedule-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">End Date *</label>
                    <input
                      type="date"
                      value={eventForm.end_date}
                      onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                      min={eventForm.event_date || new Date().toISOString().split('T')[0]}
                      max={eventForm.event_date ? (() => { const d = new Date(eventForm.event_date); d.setMonth(d.getMonth() + 6); return d.toISOString().split('T')[0]; })() : undefined}
                      className="input-field"
                      data-testid="event-end-date-input"
                    />
                    <p className="text-xs text-slate-400 mt-1">Maximum 6 months from start date</p>
                  </div>
                </>
              )}
              <button onClick={handleSubmitEvent} disabled={isLoading} className={`w-full ${isPremiumEvent ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600' : 'btn-primary'} text-white font-bold py-3 px-4 rounded-full flex items-center justify-center gap-2 transition-all`} data-testid="submit-event-btn">
                {isPremiumEvent ? <CreditCard className="w-5 h-5" /> : <Send className="w-5 h-5" />}
                {isLoading ? "Submitting..." : isPremiumEvent ? "Continue to Payment" : "Submit Event"}
              </button>
            </div>
          </div>
        )}

        {/* Stats Badge */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-4 z-[1000] bg-white/90 backdrop-blur-md rounded-full px-4 py-2 shadow-lg border border-slate-200/50" data-testid="stats-badge">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-indigo-600" />
              <span className="font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>{pins.length}</span>
              <span className="text-slate-500 text-sm">people</span>
            </div>
            <div className="flex items-center gap-2 border-l border-slate-200 pl-4">
              <Users className="w-4 h-4 text-purple-600" />
              <span className="font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>{areaData.length}</span>
              <span className="text-slate-500 text-sm">areas</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
